import type { PackageInclusionRecord as OfferPackageInclusionRecord } from '~/hooks/apis/inclusions/useGetPackageInclusionsByOffer';

import { getPackageName, getPackagePersistId } from '../OfferPackages/utils';
import { type PackageInclusionRecord, getInclusionCategory, getInclusionName, isInclusionHidden } from '../utils';

export type { PackageInclusionRecord };

export interface MasterInclusion {
  id: string;
  assignedPackageIds: Array<string>;
  category: string;
  highlighted: boolean;
  inclusion: string;
  luxPlusOnly: boolean;
  persistId?: string;
}

export interface PackageOption {
  id: string;
  name: string;
}

export const TABLE_GRID_COLUMNS = '40px minmax(150px, 180px) minmax(260px, 1fr) 110px 130px minmax(200px, 240px) 40px';

type OfferPackageInclusionWithDisplayFields = OfferPackageInclusionRecord &
  PackageInclusionRecord & {
    inclusion: OfferPackageInclusionRecord['inclusion'] & NonNullable<PackageInclusionRecord['inclusion']>;
  };

export function createMasterInclusion(packageOptions: Array<PackageOption>): MasterInclusion {
  return {
    id: `local-inclusion-${crypto.randomUUID()}`,
    assignedPackageIds: packageOptions.map((option) => option.id),
    category: 'Other',
    highlighted: false,
    inclusion: '',
    luxPlusOnly: false,
  };
}

export function getPackageOptions(packages?: Array<App.Package>): Array<PackageOption> {
  return (
    packages?.map((offerPackage, index) => ({
      id: getPackagePersistId(offerPackage) ?? `package-${index}`,
      name: getPackageName(offerPackage),
    })) ?? []
  );
}

function getInclusionKey(inclusion: OfferPackageInclusionWithDisplayFields) {
  return getInclusionName(inclusion).trim().toLowerCase();
}

function getInclusionPersistId(inclusion: OfferPackageInclusionWithDisplayFields) {
  const persistId = inclusion.inclusion_id ?? inclusion.inclusion?.id;

  return persistId ? String(persistId) : undefined;
}

export function buildMasterInclusionsFromOffer(
  packageInclusions: Record<string, Array<OfferPackageInclusionRecord>> | undefined,
): Array<MasterInclusion> {
  if (!packageInclusions) {
    return [];
  }

  const inclusionsById = new Map<string, MasterInclusion>();

  Object.entries(packageInclusions).forEach(([packageId, inclusions]) => {
    inclusions.forEach((rawInclusion) => {
      // Package inclusion payloads can include extra display fields; missing values intentionally fall back to off.
      const inclusion = rawInclusion as OfferPackageInclusionWithDisplayFields;
      const inclusionName = getInclusionName(inclusion);

      if (!inclusionName || isInclusionHidden(inclusion)) {
        return;
      }

      const inclusionKey = getInclusionKey(inclusion);
      const existingInclusion = inclusionsById.get(inclusionKey);

      if (existingInclusion) {
        if (!existingInclusion.assignedPackageIds.includes(packageId)) {
          existingInclusion.assignedPackageIds.push(packageId);
        }

        return;
      }

      inclusionsById.set(inclusionKey, {
        id: inclusionKey,
        assignedPackageIds: [packageId],
        category: getInclusionCategory(inclusion),
        highlighted: inclusion.style === 'highlighted',
        inclusion: inclusionName,
        luxPlusOnly: !!inclusion.lux_plus_tier,
        persistId: getInclusionPersistId(inclusion),
      });
    });
  });

  return Array.from(inclusionsById.values());
}

export function buildPackageInclusionsFromMaster(
  masterInclusions: Array<MasterInclusion>,
  packageOptions: Array<PackageOption>,
): Record<string, Array<PackageInclusionRecord>> {
  return packageOptions.reduce<Record<string, Array<PackageInclusionRecord>>>((packageInclusions, packageOption) => {
    const assignedInclusions = masterInclusions
      .filter((inclusion) => inclusion.inclusion.trim() && inclusion.assignedPackageIds.includes(packageOption.id))
      .map((inclusion) => ({
        isUnique: inclusion.assignedPackageIds.length === 1,
        packageInclusion: {
          id: inclusion.persistId ?? inclusion.id,
          inclusion: {
            ...(inclusion.persistId ? { id: inclusion.persistId } : {}),
            name: inclusion.inclusion,
            is_hidden: false,
            category: {
              name: inclusion.category,
            },
          },
          lux_plus_tier: inclusion.luxPlusOnly ? 'base' : null,
          style: inclusion.highlighted ? 'highlighted' : null,
        },
      }));

    packageInclusions[packageOption.id] = assignedInclusions
      .filter((inclusion) => inclusion.isUnique)
      .concat(assignedInclusions.filter((inclusion) => !inclusion.isUnique))
      .map(({ packageInclusion }) => packageInclusion);

    return packageInclusions;
  }, {});
}
